import { Directive, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Magnetic hover: the element gently drifts toward the cursor while hovered, snapping back on
 * leave. Pointer-driven, so it writes transforms directly to the DOM (no change detection) —
 * fine under zoneless. Skipped on the server, for reduced-motion users, and on touch devices.
 */
@Directive({ selector: '[appMagnetic]' })
export class MagneticDirective implements OnInit, OnDestroy {
  private readonly el: HTMLElement;
  private readonly strength = 0.35;
  private readonly cap = 14; // px of travel
  private active = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, ref: ElementRef<HTMLElement>) {
    this.el = ref.nativeElement;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (matchMedia('(hover: none)').matches) return;

    this.active = true;
    if (!this.el.style.display) this.el.style.display = 'inline-block';
    this.el.style.willChange = 'transform';
    this.el.style.transition = 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)';
    this.el.addEventListener('mousemove', this.onMove);
    this.el.addEventListener('mouseleave', this.onLeave);
  }

  ngOnDestroy(): void {
    if (!this.active) return;
    this.el.removeEventListener('mousemove', this.onMove);
    this.el.removeEventListener('mouseleave', this.onLeave);
  }

  private onMove = (e: MouseEvent): void => {
    const r = this.el.getBoundingClientRect();
    const x = clamp((e.clientX - (r.left + r.width / 2)) * this.strength, this.cap);
    const y = clamp((e.clientY - (r.top + r.height / 2)) * this.strength, this.cap);
    this.el.style.transform = `translate(${x}px, ${y}px)`;
  };

  private onLeave = (): void => {
    this.el.style.transform = '';
  };
}

function clamp(v: number, cap: number): number {
  return Math.max(-cap, Math.min(cap, v));
}
